import { Link } from 'react-router'
import { Eyebrow, GoldRule } from '#app/components/marketing/index.ts'
import { type Route } from './+types/tos.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Terms of Service · Frame & Ledger' },
	{
		name: 'description',
		content:
			'Terms governing use of frameandledger.com. Frame & Ledger LLC, a Florida limited liability company.',
	},
]

const EFFECTIVE_DATE = 'June 8, 2026'
const LAST_UPDATED = 'June 8, 2026'

export default function TermsOfServiceRoute() {
	return (
		<main className="container mx-auto px-4 pt-16 pb-24 lg:px-6 lg:pt-24">
			<header className="mx-auto max-w-3xl">
				<Eyebrow>Legal · Terms of Service</Eyebrow>
				<h1 className="mt-4 font-display text-4xl font-medium tracking-tight text-foreground sm:text-5xl">
					Terms of Service
				</h1>
				<GoldRule className="mt-6" />
				<dl className="mt-6 grid grid-cols-1 gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground sm:grid-cols-2">
					<div>
						<dt className="inline">Effective:&nbsp;</dt>
						<dd className="inline text-foreground">{EFFECTIVE_DATE}</dd>
					</div>
					<div>
						<dt className="inline">Last updated:&nbsp;</dt>
						<dd className="inline text-foreground">{LAST_UPDATED}</dd>
					</div>
				</dl>
			</header>

			<article className="mx-auto mt-12 max-w-3xl space-y-6 text-base/7 text-foreground">
				<p>
					These Terms of Service (<strong>“Terms”</strong>) are a binding agreement
					between you and <strong>Frame &amp; Ledger LLC</strong>, a Florida
					limited liability company (<strong>“Frame &amp; Ledger,” “F&amp;L,”
					“we,” “us,”</strong> or <strong>“our”</strong>), governing your access
					to and use of <strong>frameandledger.com</strong> and any related
					subdomains, pages, and content owned or operated by Frame &amp; Ledger
					(collectively, the <strong>“Website”</strong>).
				</p>
				<p>
					Please read these Terms carefully. By accessing or using the Website,
					you agree to be bound by these Terms and by our{' '}
					<Link to="/privacy" className="text-gold underline hover:no-underline">
						Privacy Policy
					</Link>
					, which is incorporated by reference. If you do not agree, you may not
					access or use the Website.
				</p>

				<Section id="about" num="1" title="About Frame & Ledger and the Website">
					<p>
						Frame &amp; Ledger designs and licenses Shopify infrastructure for
						hybrid (B2B2C) custom manufacturers. The Website exists to describe
						what we build, present case studies and methodology, and allow
						qualified business audiences to request a consultation.
					</p>
					<p>
						The Website is informational and promotional. It does not, by
						itself, create a service relationship between F&amp;L and any
						visitor, and it does not constitute an offer to enter into one.
					</p>
				</Section>

				<Section id="engagement-agreements" num="2" title="Relationship to Engagement Agreements">
					<p>
						<strong>This is important.</strong> Frame &amp; Ledger performs
						professional services only under separately executed written
						agreements with the client entity engaged. Those agreements
						typically include some combination of:
					</p>
					<ul className="ml-6 list-disc space-y-1.5">
						<li>a <strong>Master Service Agreement</strong> (MSA) or Service Agreement,</li>
						<li>an <strong>Application License Agreement</strong> (ALA) governing any licensed proprietary application,</li>
						<li>one or more <strong>Statements of Work</strong> (SOWs),</li>
						<li>an <strong>Ad Pricing Schedule</strong> (APS), where applicable, and</li>
						<li>any addenda, amendments, or change orders executed by the parties.</li>
					</ul>
					<p>
						These Terms govern your <strong>use of the Website</strong>. They do{' '}
						<strong>not</strong> govern the substance of any client engagement.
						To the extent a person or entity is engaged with F&amp;L under an
						executed engagement agreement, <strong>that engagement agreement
						controls</strong> the parties&rsquo; rights and obligations with
						respect to the services, deliverables, intellectual property,
						confidentiality, payment, liability, indemnification, dispute
						resolution, and all related matters. Nothing on the Website —
						including these Terms, marketing copy, case studies, blog posts, or
						screenshots — modifies, supersedes, or limits any executed
						engagement agreement.
					</p>
					<p>
						If there is any conflict between these Terms and an executed
						engagement agreement, the executed engagement agreement controls
						with respect to the matters it addresses.
					</p>
				</Section>

				<Section id="eligibility" num="3" title="Eligibility">
					<p>
						You may use the Website only if you are at least eighteen (18) years
						of age, are acting in a business or professional capacity, and have
						the legal capacity to enter into these Terms. By using the Website,
						you represent that you meet these requirements.
					</p>
					<p>
						The Website is intended for business audiences. It is not directed
						at consumers or children, and it is not offered for personal,
						family, or household purposes.
					</p>
				</Section>

				<Section id="license" num="4" title="License to Use the Website">
					<p>
						Subject to your compliance with these Terms, Frame &amp; Ledger
						grants you a limited, non-exclusive, non-transferable, revocable
						license to access and use the Website for your own lawful internal
						business purposes — for example, to learn about F&amp;L, evaluate
						whether to engage us, and reference our publicly available
						materials.
					</p>
					<p>This license does not include the right to:</p>
					<ol className="ml-6 list-[lower-alpha] space-y-1.5">
						<li>copy, reproduce, republish, mirror, frame, scrape, or systematically extract any portion of the Website;</li>
						<li>sell, lease, license, sublicense, or otherwise commercialize any portion of the Website or its content;</li>
						<li>modify, translate, adapt, or create derivative works of the Website or its content;</li>
						<li>reverse engineer, decompile, or attempt to derive source code or the structure, organization, or methodology underlying any application, dashboard, or system referenced on the Website;</li>
						<li>remove or alter any copyright, trademark, or other proprietary notice;</li>
						<li>use the Website to train, fine-tune, evaluate, or otherwise develop any machine-learning model, large language model, or other artificial-intelligence system, except as expressly permitted by us in writing; or</li>
						<li>use the Website in a manner inconsistent with these Terms or with applicable law.</li>
					</ol>
					<p>
						All rights not expressly granted to you are reserved by Frame &amp;
						Ledger.
					</p>
				</Section>

				<Section id="ip" num="5" title="Intellectual Property">
					<p>
						The Website and all content, software, design, layout, source code,
						written materials, graphics, photography, video, audio,
						illustrations, diagrams, architecture descriptions, methodology,
						case studies, performance figures, screenshots, recreated
						visualizations, branding (including the <strong>Frame &amp;
						Ledger</strong> name and marks, the <strong>F&amp;L</strong>{' '}
						monogram, the tagline{' '}
						<strong>&ldquo;Measure Twice. Scale Once.&rdquo;</strong>, and any
						associated trade dress), and the proprietary infrastructure
						referenced on the Website (including custom Shopify applications,
						the Native Shopify CRM module, the Ledger Dashboard, Hydrogen
						storefront patterns, and integration code and schemas) are owned by
						or licensed to Frame &amp; Ledger and are protected by copyright,
						trademark, trade-secret, and other intellectual-property laws.
					</p>
					<p>
						Frame &amp; Ledger retains all right, title, and interest in and to
						the foregoing. Nothing on the Website grants you, by implication,
						estoppel, or otherwise, any license or right to use any F&amp;L
						intellectual property except as expressly stated in these Terms or
						in an executed engagement agreement that grants such a license.
					</p>
					<p>
						Third-party marks, names, and logos referenced on the Website (for
						example, Shopify, Klaviyo, HubSpot, Sanity, Hydrogen, and any client
						business names referenced in case studies) are the property of their
						respective owners. Reference to a third-party platform or business
						does not imply endorsement by or affiliation with that platform or
						business beyond what is stated.
					</p>
				</Section>

				<Section id="submissions" num="6" title="Submissions, Inquiries, and Feedback">
					<p>
						When you submit information through the Website — for example, by
						completing the <strong>Request Consultation</strong> form, sending
						us an email, or attaching documents to a message — you represent
						that the information is accurate, that you have the authority to
						share it, and that doing so does not violate any third-party right
						or any obligation owed by you or your organization.
					</p>
					<p>
						You retain ownership of any business information you provide. By
						submitting it, you grant Frame &amp; Ledger a limited, royalty-free
						license to receive, review, store, and use the information for the
						purposes of evaluating fit, responding to your inquiry, scoping a
						potential engagement, and operating our business — in each case
						consistent with the Privacy Policy. We will not disclose information
						you mark as confidential except as permitted by the Privacy Policy
						or as required by law.
					</p>
					<p>
						If you send Frame &amp; Ledger feedback, suggestions, ideas, or
						other unsolicited input about our work, our positioning, or our
						infrastructure (<strong>“Feedback”</strong>), you grant us a
						perpetual, irrevocable, worldwide, royalty-free, fully paid-up,
						sublicensable license to use, reproduce, modify, and incorporate the
						Feedback for any lawful purpose, without obligation or attribution
						to you, except where the Feedback constitutes confidential
						information of an existing client subject to a separate written
						agreement.
					</p>
					<p>
						You should not submit, and the Website is not designed to receive,
						sensitive personal information (for example, financial-account
						credentials, health information, or government identifiers). Do not
						submit information you do not want stored or reviewed in the
						ordinary course.
					</p>
				</Section>

				<Section id="acceptable-use" num="7" title="Acceptable Use">
					<p>
						You agree not to use the Website, and not to permit any other person
						to use the Website, to:
					</p>
					<ol className="ml-6 list-[lower-alpha] space-y-1.5">
						<li>violate any law, regulation, or third-party right;</li>
						<li>infringe, misappropriate, or violate any intellectual property, privacy, publicity, or other right of any person;</li>
						<li>impersonate any person or entity, or misrepresent your affiliation with any person or entity;</li>
						<li>submit content that is unlawful, defamatory, harassing, threatening, obscene, or otherwise objectionable;</li>
						<li>transmit any virus, worm, malicious code, or other harmful component;</li>
						<li>interfere with, disrupt, probe, scan, or test the security or integrity of the Website or any infrastructure supporting it;</li>
						<li>attempt to gain unauthorized access to the Website, any account, or any associated systems;</li>
						<li>use any robot, spider, scraper, or other automated means to access the Website except for indexing by recognized search engines in accordance with our robots configuration;</li>
						<li>overload, flood, or otherwise impair the operation of the Website; or</li>
						<li>engage in any conduct that, in our reasonable judgment, exposes Frame &amp; Ledger, its clients, its personnel, or other Website users to liability or harm.</li>
					</ol>
					<p>
						We reserve the right to investigate and take appropriate action
						against any violation of this Section, including blocking access,
						removing content, and pursuing legal remedies.
					</p>
				</Section>

				<Section id="no-advice" num="8" title="No Professional Advice; Performance Figures">
					<p>
						The Website is provided for informational purposes. It is not legal,
						accounting, tax, investment, regulatory, or other professional
						advice, and it is not a substitute for the advice of a qualified
						professional engaged to assess your specific situation. Reliance on
						information on the Website is at your own risk.
					</p>
					<p>
						Case-study figures and performance metrics presented on the Website
						— including any revenue, profit, conversion-lag, or ROAS figures
						attributed to a client engagement — describe outcomes observed in a
						specific engagement under specific conditions. They are not
						promises, guarantees, or projections of results in any other
						engagement. Frame &amp; Ledger makes no representation that any
						other client will achieve similar results.
					</p>
					<p>
						Without limiting the foregoing, Frame &amp; Ledger does{' '}
						<strong>not</strong> represent or warrant that any system referenced
						on the Website, or any service Frame &amp; Ledger may provide, is
						compliant with any specific regulation, standard, or certification
						framework — including HIPAA, PCI-DSS, COPPA, GDPR-specific
						certifications, SOC 2, ISO 27001, ADA / WCAG, or industry-specific
						regimes — unless and except as expressly committed in a signed
						agreement that scopes and prices such commitments.
					</p>
				</Section>

				<Section id="third-party" num="9" title="Third-Party Links and Services">
					<p>
						The Website may contain links to third-party websites, services, or
						content (for example, LinkedIn profiles, video hosts, partner
						platforms, or referenced client websites). Those resources are not
						controlled by Frame &amp; Ledger, and we are not responsible for
						their content, accuracy, availability, security, or practices. The
						presence of a link or reference does not imply endorsement. Your use
						of any third-party resource is at your own risk and is governed by
						the terms and policies of that resource.
					</p>
				</Section>

				<Section id="disclaimers" num="10" title="Disclaimers">
					<LegalCaps>
						Except as expressly stated in a signed agreement between you and
						Frame &amp; Ledger, the Website and all content, materials, and
						information on or accessible through it are provided “as is” and “as
						available,” with all faults and without warranty of any kind.
					</LegalCaps>
					<LegalCaps>
						To the fullest extent permitted by applicable law, Frame &amp;
						Ledger and its members, officers, employees, contractors, agents,
						and licensors (collectively, the <strong>“F&amp;L Parties”</strong>)
						disclaim all warranties, express, implied, and statutory, including
						warranties of merchantability, fitness for a particular purpose,
						non-infringement, accuracy, reliability, availability, title, and
						any warranties arising from course of dealing, course of
						performance, or usage of trade.
					</LegalCaps>
					<LegalCaps>
						The F&amp;L Parties do not warrant that the Website or its content
						will be uninterrupted, error-free, accurate, complete, secure, or
						free of viruses or other harmful components, or that defects will be
						corrected. Any material downloaded or otherwise obtained through the
						Website is accessed at your own risk, and you are solely responsible
						for any damage to your system or loss of data resulting from such
						access.
					</LegalCaps>
					<LegalCaps>
						Some jurisdictions do not allow the exclusion of certain warranties,
						so some of the exclusions above may not apply to you. In that case,
						the exclusions apply to the maximum extent permitted by law.
					</LegalCaps>
				</Section>

				<Section id="liability" num="11" title="Limitation of Liability">
					<LegalCaps>
						To the fullest extent permitted by applicable law, in no event will
						any of the F&amp;L Parties be liable to you or any third party for
						any indirect, incidental, special, consequential, exemplary, or
						punitive damages, or for any loss of profits, revenue, business,
						goodwill, data, or opportunity, arising out of or relating to your
						access to or use of the Website or these Terms, even if advised of
						the possibility of such damages and regardless of the theory of
						liability (contract, tort, strict liability, or otherwise).
					</LegalCaps>
					<LegalCaps>
						Without limiting the foregoing, the aggregate liability of the
						F&amp;L Parties to you for all claims arising out of or relating to
						your access to or use of the Website or these Terms will not exceed
						one hundred U.S. dollars (USD $100.00).
					</LegalCaps>
					<p>
						If you are engaged with Frame &amp; Ledger under an executed
						engagement agreement, the limitation of liability provisions of that
						engagement agreement govern that engagement and are not affected by
						this Section. Conversely, nothing in your engagement agreement
						modifies the limitations in this Section as applied to your use of
						the Website.
					</p>
					<LegalCaps>
						Some jurisdictions do not allow the limitation or exclusion of
						liability for incidental or consequential damages, so the above
						limitation may not apply to you. In that case, liability is limited
						to the greatest extent permitted by applicable law.
					</LegalCaps>
				</Section>

				<Section id="indemnification" num="12" title="Indemnification">
					<p>
						You agree to defend, indemnify, and hold harmless the F&amp;L
						Parties from and against any third-party claim, demand, action,
						loss, liability, damage, cost, or expense (including reasonable
						attorneys&rsquo; fees) arising out of or relating to: (a) your
						access to or use of the Website; (b) your breach of these Terms; (c)
						your violation of any law or third-party right in connection with
						your use of the Website; or (d) any content or information you
						submit through the Website.
					</p>
					<p>
						Frame &amp; Ledger may, at its option, assume the exclusive defense
						and control of any matter otherwise subject to indemnification by
						you, in which case you will reasonably cooperate with our defense.
						You will not settle any claim affecting the F&amp;L Parties without
						our prior written consent.
					</p>
				</Section>

				<Section id="modifications" num="13" title="Modifications to the Website and to These Terms">
					<p>
						Frame &amp; Ledger may modify, update, suspend, or discontinue any
						part of the Website at any time, with or without notice. We may also
						update these Terms from time to time. When we do, we will revise the
						“Last Updated” date at the top of these Terms and, where changes are
						material, take reasonable additional steps to notify users. Your
						continued use of the Website after an update constitutes acceptance
						of the updated Terms. If you do not agree to the updated Terms, you
						must stop using the Website.
					</p>
				</Section>

				<Section id="governing-law" num="14" title="Governing Law and Venue">
					<p>
						These Terms and any non-contractual obligations arising out of or in
						connection with them are governed by the laws of the{' '}
						<strong>State of Florida</strong>, without regard to its
						conflict-of-laws principles, and excluding the United Nations
						Convention on Contracts for the International Sale of Goods.
					</p>
					<p>
						Any dispute, claim, or controversy arising out of or relating to
						these Terms or the Website that is not resolved through the
						good-faith discussions described in Section 15 will be brought
						exclusively in the state or federal courts located in Florida. You
						consent to the personal jurisdiction of those courts and waive any
						objection based on inconvenient forum.
					</p>
				</Section>

				<Section id="disputes" num="15" title="Dispute Resolution">
					<p>
						Before initiating any formal proceeding arising out of or relating
						to these Terms or the Website, the parties will first attempt in
						good faith to resolve the matter through informal discussion. Either
						party may initiate this process by sending written notice describing
						the dispute and the relief sought to the other party (in our case,
						to{' '}
						<a
							href="mailto:legal@frameandledger.com"
							className="text-gold underline hover:no-underline"
						>
							legal@frameandledger.com
						</a>
						). If the dispute is not resolved within thirty (30) days after such
						notice, either party may pursue any remedies available at law or in
						equity, subject to Section 14.
					</p>
					<p>
						Each party irrevocably waives, to the fullest extent permitted by
						law, any right to a trial by jury in any proceeding arising out of
						or relating to these Terms or the Website.
					</p>
					<p>
						You and Frame &amp; Ledger agree that any claim arising out of or
						relating to these Terms or the Website must be brought in an
						individual capacity, and not as a plaintiff or class member in any
						purported class, collective, or representative proceeding.
					</p>
					<p>
						Nothing in this Section limits either party&rsquo;s right to seek
						injunctive or other equitable relief in a court of competent
						jurisdiction to protect intellectual property, confidential
						information, or other rights for which monetary damages would be
						inadequate.
					</p>
				</Section>

				<Section id="termination" num="16" title="Termination of Access">
					<p>
						We may suspend or terminate your access to the Website at any time,
						with or without notice, for any reason — including suspected
						violation of these Terms or any conduct we determine, in our sole
						discretion, is harmful to Frame &amp; Ledger, its clients, or other
						users. Upon termination, the licenses granted to you under these
						Terms cease, and any Sections of these Terms that by their nature
						should survive termination will survive (including Sections 2, 5, 6,
						8, 10, 11, 12, 14, 15, 17, and 18).
					</p>
				</Section>

				<Section id="general" num="17" title="General">
					<dl className="space-y-4">
						<DefItem term="No agency.">
							Nothing in these Terms creates any partnership, joint venture,
							employment, agency, or fiduciary relationship between you and
							Frame &amp; Ledger. Neither party has authority to bind the other.
						</DefItem>
						<DefItem term="Assignment.">
							You may not assign or transfer these Terms, by operation of law or
							otherwise, without our prior written consent. Frame &amp; Ledger
							may assign or transfer these Terms without restriction. Any
							attempted assignment in violation of this provision is null and
							void.
						</DefItem>
						<DefItem term="Severability.">
							If any provision of these Terms is held to be unenforceable, that
							provision will be enforced to the maximum extent permitted, and
							the remaining provisions will remain in full force and effect.
						</DefItem>
						<DefItem term="No waiver.">
							Our failure to enforce any provision of these Terms is not a
							waiver of that or any other provision.
						</DefItem>
						<DefItem term="Force majeure.">
							Frame &amp; Ledger will not be liable for any failure or delay in
							performance caused by circumstances beyond its reasonable control.
						</DefItem>
						<DefItem term="Notices.">
							We may give notices under these Terms by posting on the Website,
							by email to an address you provide, or by other reasonable means.
							You may give notices to us at{' '}
							<a
								href="mailto:legal@frameandledger.com"
								className="text-gold underline hover:no-underline"
							>
								legal@frameandledger.com
							</a>
							.
						</DefItem>
						<DefItem term="Headings.">
							Section headings are for convenience and do not affect
							interpretation.
						</DefItem>
					</dl>
				</Section>

				<Section id="entire-agreement" num="18" title="Entire Agreement">
					<p>
						These Terms, together with the Privacy Policy and any other terms
						expressly incorporated by reference, constitute the entire agreement
						between you and Frame &amp; Ledger with respect to your use of the
						Website, and supersede all prior or contemporaneous communications
						and proposals concerning that subject matter.
					</p>
					<p>
						For the avoidance of doubt: these Terms govern your{' '}
						<strong>use of the Website</strong>. They are separate from, and do
						not modify, any executed engagement agreement between Frame &amp;
						Ledger and a client entity. The engagement agreement controls the
						substance and conduct of the client engagement.
					</p>
				</Section>

				<Section id="contact" num="19" title="Contact">
					<p>
						Questions, notices, and concerns about these Terms can be directed
						to:
					</p>
					<address className="not-italic">
						<strong>Frame &amp; Ledger LLC</strong>
						<br />
						Attn: Legal
						<br />
						<a
							href="mailto:legal@frameandledger.com"
							className="text-gold underline hover:no-underline"
						>
							legal@frameandledger.com
						</a>
					</address>
				</Section>

				<footer className="mt-12 border-t border-border pt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
					<p>Frame &amp; Ledger LLC · Florida · 2026</p>
					<p className="mt-2">⬢ The Frame is set. The Ledger is clear.</p>
				</footer>
			</article>
		</main>
	)
}

function Section({
	id,
	num,
	title,
	children,
}: {
	id: string
	num: string
	title: string
	children: React.ReactNode
}) {
	return (
		<section id={id} className="scroll-mt-24 pt-8">
			<h2 className="flex items-baseline gap-3 font-display text-2xl font-medium tracking-tight text-foreground sm:text-3xl">
				<span className="text-gold">§ {num.padStart(2, '0')}</span>
				<span>{title}</span>
			</h2>
			<div className="mt-4 space-y-4">{children}</div>
		</section>
	)
}

function LegalCaps({ children }: { children: React.ReactNode }) {
	return (
		<p className="rounded-sm border-l-2 border-gold/40 bg-muted/30 px-4 py-3 text-sm uppercase tracking-[0.04em] text-muted-foreground">
			{children}
		</p>
	)
}

function DefItem({ term, children }: { term: string; children: React.ReactNode }) {
	return (
		<div>
			<dt className="inline font-semibold text-foreground">{term}&nbsp;</dt>
			<dd className="inline">{children}</dd>
		</div>
	)
}
